import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Bell, Mail, MessageSquare, Settings, Smartphone } from "lucide-react";
import { PhoneVerificationDialog } from "@/components/settings/PhoneVerificationDialog";
import { SMSConsentModal } from "@/components/settings/SMSConsentModal";

const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.lanerunner.app";

/**
 * Quick channel on/off toggles for the LR Direct dashboards.
 * Wires to the same profiles.notification_* master flags Profile Settings uses.
 */
export function AlertSettingsWidget() {
  const { user, profile, refreshProfile } = useAuth() as any;
  const [showConsent, setShowConsent] = useState(false);
  const [showPhone, setShowPhone] = useState(false);
  const [pendingConsentId, setPendingConsentId] = useState<string | null>(null);
  const [pendingPhone, setPendingPhone] = useState<string | null>(null);
  // Optimistic overlay: keeps the tile on the value we just saved so it never
  // flickers back to a stale profile value while refreshProfile() is in flight.
  const [optimistic, setOptimistic] = useState<Record<string, any>>({});

  const val = (key: string, fallback: boolean) =>
    key in optimistic ? !!optimistic[key] : (profile?.[key] ?? fallback);

  const emailOn = val("notification_email", true);
  const smsOn = val("notification_sms_enabled", false);
  const pushOn = val("notification_push_enabled", true);
  const phoneVerified = val("phone_verified", false);

  const persist = async (patch: Record<string, any>) => {
    if (!user) return;
    setOptimistic((prev) => ({ ...prev, ...patch }));
    const { error } = await supabase.from("profiles").update(patch).eq("id", user.id);
    if (error) {
      // Roll the optimistic keys back on failure.
      setOptimistic((prev) => {
        const next = { ...prev };
        for (const k of Object.keys(patch)) delete next[k];
        return next;
      });
      toast.error("Couldn't save preference");
      return;
    }
    if (typeof refreshProfile === "function") await refreshProfile();
  };

  const onToggleSms = (on: boolean) => {
    if (!on) {
      persist({ notification_sms_enabled: false });
      return;
    }
    if (phoneVerified) {
      persist({ notification_sms_enabled: true });
      return;
    }
    setShowConsent(true);
  };

  return (
    <Card className="p-4">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Bell className="w-4 h-4 text-brand-teal" />
          <h3 className="text-sm font-semibold">Alert Settings</h3>
        </div>
        <Link
          to="/profile-settings?tab=notifications"
          className="text-xs font-medium text-brand-teal hover:underline inline-flex items-center gap-1"
        >
          <Settings className="w-3 h-3" />
          Customize your alerts →
        </Link>
      </div>

      <p className="text-xs text-muted-foreground mb-4">
        Stay on top of your freight. Turn on the alerts that matter to you so you never
        miss a bid, award, or new load. Fine-tune each one in Settings to get exactly
        what you want — no more, no less.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <ToggleTile
          icon={<Bell className="w-4 h-4" />}
          title="App"
          subtitle="Home/lock-screen alerts on the app"
          on={pushOn}
          onChange={(v) => persist({ notification_push_enabled: v })}
        />
        <ToggleTile
          icon={<Mail className="w-4 h-4" />}
          title="Email"
          subtitle="Always-on delivery"
          on={emailOn}
          onChange={(v) => persist({ notification_email: v })}
        />
        <ToggleTile
          icon={<MessageSquare className="w-4 h-4" />}
          title="SMS"
          subtitle={
            phoneVerified
              ? "Awards, new loads, and quote requests"
              : "Requires phone verification"
          }
          on={smsOn && phoneVerified}
          onChange={onToggleSms}
        />
      </div>

      <div className="mt-4 rounded-md border border-dashed border-brand-teal/40 bg-brand-teal/5 p-3 flex items-start gap-2">
        <Smartphone className="w-4 h-4 text-brand-teal mt-0.5 shrink-0" />
        <p className="text-xs text-foreground">
          For instant alerts, we recommend the LaneRunner app —{" "}
          <a
            href={PLAY_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-brand-teal hover:underline"
          >
            get it on Google Play
          </a>
          . <span className="text-muted-foreground">(iOS coming soon)</span>
        </p>
      </div>

      <SMSConsentModal
        open={showConsent}
        onOpenChange={setShowConsent}
        initialPhone={profile?.phone ?? null}
        onConsented={(consentId, phoneE164) => {
          setPendingConsentId(consentId);
          setPendingPhone(phoneE164);
          setShowPhone(true);
        }}
      />

      <PhoneVerificationDialog
        open={showPhone}
        onOpenChange={setShowPhone}
        defaultPhone={pendingPhone}
        onVerified={async () => {
          if (pendingConsentId) {
            await supabase.rpc("mark_sms_consent_otp_verified", {
              p_consent_id: pendingConsentId,
            });
          }
          setPendingConsentId(null);
          setPendingPhone(null);
          setOptimistic((prev) => ({ ...prev, phone_verified: true }));
          await persist({ notification_sms_enabled: true });
        }}
        onCancel={async () => {
          setPendingConsentId(null);
          setPendingPhone(null);
        }}
      />
    </Card>
  );
}

interface TileProps {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  on: boolean;
  onChange: (v: boolean) => void;
}
function ToggleTile({ icon, title, subtitle, on, onChange }: TileProps) {
  return (
    <div
      className={`rounded-lg border p-3 flex flex-col gap-2 transition-colors ${
        on ? "border-brand-teal bg-brand-teal/5" : "border-border bg-background"
      }`}
    >
      <div className="flex items-center justify-between">
        <div
          className={`flex items-center gap-1.5 text-sm font-medium ${
            on ? "text-brand-teal" : "text-foreground"
          }`}
        >
          {icon}
          {title}
        </div>
        <Switch checked={on} onCheckedChange={onChange} />
      </div>
      <p className="text-[11px] text-muted-foreground leading-snug">{subtitle}</p>
    </div>
  );
}
